// Utility function for rounding success rates to 2 decimal places
const roundToTwoDecimals = (value: number): number => {
  return Math.round(value * 100) / 100;
};

interface PatternData {
  pattern: string;
  frequency: number;
  success_rate: number;
  next_win_count: number;
  next_loss_count: number;
  total_sequences: number; // next_win_count + next_loss_count
}

interface ComparisonSourceInfo {
  type: 'trading_file' | 'honest_simulation';
  id: string;
  name: string;
  analysis_mode?: string;
}

interface PatternComparisonResult {
  pattern: string;
  source1_data?: PatternData;
  source2_data?: PatternData;
  status: 'identical' | 'different_values' | 'only_in_source1' | 'only_in_source2';
  differences?: {
    frequency_diff?: number;
    success_rate_diff?: number;
    total_sequences_diff?: number;
  };
}

interface ComparisonSummary {
  total_patterns_source1: number;
  total_patterns_source2: number;
  identical_patterns: number;
  different_value_patterns: number;
  only_in_source1: number;
  only_in_source2: number;
  comparison_details: PatternComparisonResult[];
}

export class CrossPatternComparisonService {
  
  // Get trading files available for comparison
  static async getTradingFiles(): Promise<{ success: boolean; data?: Array<{id: string, filename: string, analysis_modes: string[]}>, error?: string }> {
    try {
      const { data: files, error } = await supabase
        .from('trading_files')
        .select('id, filename')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching trading files:', error);
        return { success: false, error: error.message };
      }

      // Get available analysis modes for each file
      const filesWithModes = await Promise.all(
        files.map(async (file) => {
          const { data: patterns } = await supabase
            .from('trading_patterns')
            .select('analysis_mode')
            .eq('file_id', file.id);
          
          const uniqueModes = [...new Set(patterns?.map(p => p.analysis_mode) || [])];
          
          return {
            id: file.id,
            filename: file.filename,
            analysis_modes: uniqueModes
          };
        })
      );

      return { success: true, data: filesWithModes };
    } catch (error) {
      console.error('Service error:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Get honest backtest simulations available for comparison
  static async getHonestBacktestSimulations(): Promise<{ success: boolean; data?: Array<{id: string, name: string, filename: string}>, error?: string }> {
    try {
      const { data: simulations, error } = await supabase
        .from('honest_backtest_simulations')
        .select(`
          id, 
          user_id,
          file_id,
          created_at,
          trading_files!inner(filename)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching honest backtest simulations:', error);
        return { success: false, error: error.message };
      }

      const formattedData = simulations.map(sim => ({
        id: sim.id,
        name: `Simulation ${sim.id.split('-')[0]}`,
        filename: (sim as any).trading_files.filename
      }));

      return { success: true, data: formattedData };
    } catch (error) {
      console.error('Service error:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Compare patterns between two sources
  static async comparePatterns(
    source1: ComparisonSourceInfo,
    source2: ComparisonSourceInfo
  ): Promise<{ success: boolean; data?: ComparisonSummary; error?: string }> {
    try {
      console.log('🔄 Starting pattern comparison:', { source1, source2 });

      // Fetch patterns from both sources
      const [patterns1Result, patterns2Result] = await Promise.all([
        this.fetchPatternsFromSource(source1),
        this.fetchPatternsFromSource(source2)
      ]);

      if (!patterns1Result.success || !patterns2Result.success) {
        return { 
          success: false, 
          error: patterns1Result.error || patterns2Result.error || 'Failed to fetch patterns'
        };
      }

      const patterns1 = patterns1Result.data || [];
      const patterns2 = patterns2Result.data || [];

      console.log(`📊 Loaded ${patterns1.length} patterns from source 1, ${patterns2.length} from source 2`);

      // Create pattern maps for efficient comparison
      const patterns1Map = new Map<string, PatternData>();
      const patterns2Map = new Map<string, PatternData>();

      patterns1.forEach(p => patterns1Map.set(p.pattern, p));
      patterns2.forEach(p => patterns2Map.set(p.pattern, p));

      // Get all unique patterns
      const allPatterns = new Set([...patterns1Map.keys(), ...patterns2Map.keys()]);
      
      const comparisonResults: PatternComparisonResult[] = [];
      let identicalCount = 0;
      let differentValuesCount = 0;
      let onlyInSource1Count = 0;
      let onlyInSource2Count = 0;

      for (const pattern of allPatterns) {
        const source1Data = patterns1Map.get(pattern);
        const source2Data = patterns2Map.get(pattern);

        if (source1Data && source2Data) {
          // Pattern exists in both sources - use rounded success rates for comparison
          const isIdentical = 
            source1Data.frequency === source2Data.frequency &&
            roundToTwoDecimals(source1Data.success_rate) === roundToTwoDecimals(source2Data.success_rate) &&
            source1Data.total_sequences === source2Data.total_sequences;

          if (isIdentical) {
            identicalCount++;
            comparisonResults.push({
              pattern,
              source1_data: source1Data,
              source2_data: source2Data,
              status: 'identical'
            });
          } else {
            differentValuesCount++;
            comparisonResults.push({
              pattern,
              source1_data: source1Data,
              source2_data: source2Data,
              status: 'different_values',
              differences: {
                frequency_diff: source2Data.frequency - source1Data.frequency,
                success_rate_diff: roundToTwoDecimals(source2Data.success_rate - source1Data.success_rate),
                total_sequences_diff: source2Data.total_sequences - source1Data.total_sequences
              }
            });
          }
        } else if (source1Data && !source2Data) {
          // Pattern only in source 1
          onlyInSource1Count++;
          comparisonResults.push({
            pattern,
            source1_data: source1Data,
            status: 'only_in_source1'
          });
        } else if (!source1Data && source2Data) {
          // Pattern only in source 2
          onlyInSource2Count++;
          comparisonResults.push({
            pattern,
            source2_data: source2Data,
            status: 'only_in_source2'
          });
        }
      }

      // Sort results by pattern for consistency
      comparisonResults.sort((a, b) => a.pattern.localeCompare(b.pattern));

      const summary: ComparisonSummary = {
        total_patterns_source1: patterns1.length,
        total_patterns_source2: patterns2.length,
        identical_patterns: identicalCount,
        different_value_patterns: differentValuesCount,
        only_in_source1: onlyInSource1Count,
        only_in_source2: onlyInSource2Count,
        comparison_details: comparisonResults
      };

      console.log('✅ Pattern comparison completed:', summary);
      return { success: true, data: summary };

    } catch (error) {
      console.error('❌ Error in pattern comparison:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error during pattern comparison'
      };
    }
  }

  // Helper method to fetch patterns from a source with pagination
  private static async fetchPatternsFromSource(source: ComparisonSourceInfo): Promise<{ success: boolean; data?: PatternData[]; error?: string }> {
    try {
      const timestamp = Date.now();
      console.log(`🔍 [PAGINATION] Starting fetch from ${source.type}:`, {
        sourceId: source.id,
        sourceName: source.name,
        analysisMode: source.analysis_mode,
        timestamp
      });

      let allPatterns: PatternData[] = [];
      let offset = 0;
      const batchSize = 1000; // Fetch in chunks of 1000
      let hasMore = true;

      if (source.type === 'trading_file') {
        console.log(`🔍 [PAGINATION] Starting pagination for trading_patterns...`);
        
        while (hasMore) {
          console.log(`🔍 [PAGINATION] Fetching batch: offset=${offset}, limit=${batchSize}`);
          
          const { data: patterns, error } = await supabase
            .from('trading_patterns')
            .select('pattern, frequency, success_rate, next_win_count, next_loss_count')
            .eq('file_id', source.id)
            .eq('analysis_mode', source.analysis_mode || 'basic')
            .range(offset, offset + batchSize - 1)
            .order('pattern');

          if (error) {
            console.error(`❌ [PAGINATION] Database error at offset ${offset}:`, error);
            return { success: false, error: error.message };
          }

          if (!patterns || patterns.length === 0) {
            console.log(`✅ [PAGINATION] No more patterns found, stopping at offset ${offset}`);
            hasMore = false;
            break;
          }

          console.log(`✅ [PAGINATION] Fetched ${patterns.length} patterns in this batch`);
          
          const formattedBatch = patterns.map(p => ({
            pattern: p.pattern,
            frequency: p.frequency,
            success_rate: roundToTwoDecimals(p.success_rate),
            next_win_count: p.next_win_count,
            next_loss_count: p.next_loss_count,
            total_sequences: p.next_win_count + p.next_loss_count
          }));

          allPatterns.push(...formattedBatch);
          
          // If we got less than batchSize, we're done
          if (patterns.length < batchSize) {
            hasMore = false;
          } else {
            offset += batchSize;
          }
        }

        console.log(`✅ [PAGINATION] Total patterns fetched from trading_file: ${allPatterns.length}`);
        return { success: true, data: allPatterns };

      } else if (source.type === 'honest_simulation') {
        console.log(`🔍 [PAGINATION] Starting pagination for honest_backtest_patterns...`);
        
        while (hasMore) {
          console.log(`🔍 [PAGINATION] Fetching batch: offset=${offset}, limit=${batchSize}`);
          
          const { data: patterns, error } = await supabase
            .from('honest_backtest_patterns')
            .select('pattern, frequency, success_rate, next_win_count, next_loss_count')
            .eq('simulation_id', source.id)
            .range(offset, offset + batchSize - 1)
            .order('pattern');

          if (error) {
            console.error(`❌ [PAGINATION] Database error at offset ${offset}:`, error);
            return { success: false, error: error.message };
          }

          if (!patterns || patterns.length === 0) {
            console.log(`✅ [PAGINATION] No more patterns found, stopping at offset ${offset}`);
            hasMore = false;
            break;
          }

          console.log(`✅ [PAGINATION] Fetched ${patterns.length} patterns in this batch`);
          
          const formattedBatch = patterns.map(p => ({
            pattern: p.pattern,
            frequency: p.frequency,
            success_rate: roundToTwoDecimals(p.success_rate),
            next_win_count: p.next_win_count,
            next_loss_count: p.next_loss_count,
            total_sequences: p.next_win_count + p.next_loss_count
          }));

          allPatterns.push(...formattedBatch);
          
          // If we got less than batchSize, we're done
          if (patterns.length < batchSize) {
            hasMore = false;
          } else {
            offset += batchSize;
          }
        }

        console.log(`✅ [PAGINATION] Total patterns fetched from honest_simulation: ${allPatterns.length}`);
        return { success: true, data: allPatterns };
      }

      return { success: false, error: 'Invalid source type' };
      
    } catch (error) {
      console.error('❌ [PAGINATION] Error fetching patterns from source:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }
}

// Import supabase
import { supabase } from '@/integrations/supabase/client';